import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion, useScroll } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  Eye,
  EyeOff,
  Library as LibraryIcon,
  ListTree,
} from "lucide-react";
import lessons from "@/data/lessons.json";
import { DOMAINS, LEVELS } from "@/data/site";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BottomSheet from "@/components/BottomSheet";

const TOC = [
  { id: "passage", label: "النص للقراءة" },
  { id: "vocab", label: "قائمة المصطلحات" },
  { id: "speaking", label: "اختبار المحادثة" },
  { id: "answers", label: "الأجوبة النموذجية" },
];

const Paragraphs = ({ text, className = "" }) => (
  <div className={className}>
    {String(text)
      .split("\n\n")
      .map((p, i) => (
        <p key={i} className="mb-4 last:mb-0">
          {p}
        </p>
      ))}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="mb-6 border-b-2 border-gold pb-2.5 text-xl font-bold text-navy-900 sm:text-2xl">{children}</h2>
);

const LessonPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const [showAnswers, setShowAnswers] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [active, setActive] = useState("passage");

  const idx = lessons.findIndex((l) => l.id === id);
  const lesson = lessons[idx];

  const levelItems = useMemo(() => lessons.filter((l) => lesson && l.level === lesson.level), [lesson]);
  const prev = idx > 0 && lessons[idx - 1].level === lesson.level ? lessons[idx - 1] : null;
  const next = idx < lessons.length - 1 && lessons[idx + 1].level === lesson.level ? lessons[idx + 1] : null;
  const domain = lesson ? DOMAINS[lesson.domain] || DOMAINS.education : null;
  const level = lesson ? LEVELS[lesson.level] : null;

  useEffect(() => {
    setShowAnswers(false);
    setActive("passage");
  }, [id]);

  useEffect(() => {
    const onScroll = () => {
      let cur = "passage";
      TOC.forEach((t) => {
        const el = document.getElementById(t.id);
        if (el && el.getBoundingClientRect().top <= 150) cur = t.id;
      });
      setActive(cur);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!lesson) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-paper px-6 text-center" dir="rtl">
        <p className="text-lg font-bold text-navy-900">لم يتم العثور على هذا الدرس</p>
        <Link to="/" data-testid="lesson-notfound-home" className="min-h-[48px] rounded-xl bg-navy-900 px-6 py-3 font-bold text-ink">
          العودة إلى الرئيسية
        </Link>
      </div>
    );
  }

  const jump = (sid) => {
    setTocOpen(false);
    setTimeout(() => document.getElementById(sid)?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  const speaking = lesson.speaking || {};
  const answers = lesson.answers || {};

  return (
    <div dir="rtl" className="min-h-screen bg-paper text-navy-900">
      <motion.div
        style={{ scaleX: scrollYProgress }}
        className="fixed inset-x-0 top-0 z-[60] h-1 origin-right bg-teal-600"
        data-testid="lesson-progress-bar"
      />
      <Navbar light />

      <header className="container-x pt-24 sm:pt-28">
        <button
          data-testid="lesson-back-level"
          onClick={() => navigate("/", { state: { to: "library" } })}
          className="mb-6 flex min-h-[44px] items-center gap-2 text-sm font-semibold text-navy-900/70 transition hover:text-teal-600"
        >
          <ChevronLeft className="h-4 w-4" />
          العودة إلى مكتبة الدروس
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <span
            data-testid="lesson-level-badge"
            className="rounded-full px-3 py-1 font-en text-xs font-bold"
            style={{ color: level.color, backgroundColor: `${level.color}22` }}
          >
            المستوى {lesson.level} — {level.name}
          </span>
          <span className="flex items-center gap-1.5 rounded-full bg-navy-900/6 px-3 py-1 text-xs font-semibold text-navy-900/70">
            <domain.icon className="h-3.5 w-3.5" />
            {domain.ar}
          </span>
        </div>
        <h1
          dir="ltr"
          data-testid="lesson-title"
          className="mt-4 text-left font-en text-2xl font-extrabold leading-snug sm:text-4xl"
        >
          {lesson.title}
        </h1>
        <p data-testid="lesson-subtitle" className="mt-3 text-sm leading-relaxed text-navy-900/60 sm:text-base">
          {lesson.subtitle}
        </p>
        <hr className="mt-8 border-navy-900/10" />
      </header>

      <div className="container-x grid gap-10 pb-28 pt-10 lg:grid-cols-[1fr_260px]">
        <main className="min-w-0">
          <section id="passage" className="scroll-mt-24">
            <SectionTitle>النص / Reading Passage</SectionTitle>
            <div className="en-ltr rounded-2xl border border-navy-900/10 bg-white p-6 font-serifen text-[1.02rem] leading-[1.95] text-[#26324B] sm:p-8">
              {lesson.passage.map((p, i) => (
                <p key={i} className="mb-5 last:mb-0">
                  {p}
                </p>
              ))}
            </div>
          </section>

          <section id="vocab" className="mt-14 scroll-mt-24">
            <SectionTitle>القائمة الكاملة للمصطلحات / Full Vocabulary List</SectionTitle>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {lesson.vocab.map((v, i) => (
                <div
                  key={i}
                  data-testid={`lesson-vocab-item-${i}`}
                  className="flex items-start gap-3 rounded-xl border border-navy-900/8 bg-white p-4"
                >
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-teal-500/10 font-en text-xs font-bold text-teal-700">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p dir="ltr" className="text-left font-en text-[15px] font-bold text-navy-900">{v.en}</p>
                    <p className="mt-0.5 text-sm text-navy-900/65">{v.ar}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="speaking" className="mt-14 scroll-mt-24">
            <SectionTitle>اختبار محاكاة IELTS Speaking (بدون أجوبة)</SectionTitle>
            <div className="rounded-2xl border border-navy-900/10 bg-white p-6 sm:p-8">
              {speaking.part1?.length > 0 && (
                <div className="mb-8">
                  <h3 className="mb-3 font-en text-base font-bold text-gold-strong">Part 1</h3>
                  <ol dir="ltr" className="list-decimal space-y-2.5 pl-6 text-left font-serifen text-[15px] leading-relaxed text-[#26324B]">
                    {speaking.part1.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ol>
                </div>
              )}

              {speaking.part2 && (
                <div className="mb-8">
                  <h3 className="mb-3 font-en text-base font-bold text-gold-strong">Part 2 — Cue Card</h3>
                  <div dir="ltr" className="rounded-xl bg-teal-500/10 p-5 text-left" style={{ borderLeft: "4px solid #0D9488" }}>
                    <p className="font-serifen text-[15px] font-bold text-navy-900">{speaking.part2.task}</p>
                    <p className="mt-2 font-serifen text-sm text-navy-900/70">You should say:</p>
                    <ul className="mt-2 list-disc space-y-1.5 pl-6 font-serifen text-[15px] text-[#26324B]">
                      {speaking.part2.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </div>
                  {speaking.part2.followUps?.length > 0 && (
                    <ol dir="ltr" className="mt-5 list-decimal space-y-2.5 pl-6 text-left font-serifen text-[15px] leading-relaxed text-[#26324B]">
                      {speaking.part2.followUps.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ol>
                  )}
                </div>
              )}

              {speaking.part3?.length > 0 && (
                <div>
                  <h3 className="mb-3 font-en text-base font-bold text-gold-strong">Part 3</h3>
                  <ol dir="ltr" className="list-decimal space-y-2.5 pl-6 text-left font-serifen text-[15px] leading-relaxed text-[#26324B]">
                    {speaking.part3.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </section>

          <section id="answers" className="mt-14 scroll-mt-24">
            <SectionTitle>الأجوبة النموذجية</SectionTitle>
            <button
              data-testid="lesson-answers-toggle"
              onClick={() => setShowAnswers((s) => !s)}
              className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-teal-700 text-base font-bold text-white transition hover:bg-teal-600"
            >
              {showAnswers ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              {showAnswers ? "إخفاء الأجوبة النموذجية" : "إظهار الأجوبة النموذجية"}
            </button>

            <AnimatePresence initial={false}>
              {showAnswers && (
                <motion.div
                  key="answers-panel"
                  data-testid="lesson-answers-panel"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="mt-6 rounded-2xl border border-navy-900/10 bg-white p-6 sm:p-8">
                    {["part1", "part2", "part3"].map((pk) =>
                      answers[pk]?.length > 0 ? (
                        <div key={pk} className="mb-8 last:mb-0">
                          <h4 className="mb-4 font-en text-base font-bold text-teal-700">
                            Part {pk.slice(-1)}
                          </h4>
                          {answers[pk].map((item, i) => (
                            <div key={i} className="mb-6 last:mb-0">
                              <p dir="ltr" className="text-left font-en text-[15px] font-bold text-navy-900">{item.q}</p>
                              <Paragraphs
                                text={item.a}
                                className="en-ltr mt-2 border-l-[3px] border-gold pl-4 font-serifen text-[15px] leading-[1.9] text-[#37415A]"
                              />
                            </div>
                          ))}
                        </div>
                      ) : null
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>

          <nav className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="lesson-prev-next">
            {prev ? (
              <Link
                to={`/lessons/${prev.id}`}
                data-testid="lesson-prev"
                className="group rounded-2xl border border-navy-900/10 bg-white p-5 transition hover:border-teal-600/50"
              >
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-navy-900/50">
                  <ArrowRight className="h-4 w-4" />
                  الدرس السابق
                </p>
                <p dir="ltr" className="text-left font-en text-sm font-bold leading-snug group-hover:text-teal-700">{prev.title}</p>
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link
                to={`/lessons/${next.id}`}
                data-testid="lesson-next"
                className="group rounded-2xl border border-navy-900/10 bg-white p-5 text-left transition hover:border-teal-600/50 sm:text-right"
              >
                <p className="mb-1.5 flex items-center justify-end gap-1.5 text-xs font-semibold text-navy-900/50">
                  الدرس التالي
                  <ArrowLeft className="h-4 w-4" />
                </p>
                <p dir="ltr" className="text-left font-en text-sm font-bold leading-snug group-hover:text-teal-700">{next.title}</p>
              </Link>
            )}
          </nav>
        </main>

        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-navy-900/10 bg-white p-5">
            <p className="mb-4 flex items-center gap-2 text-sm font-bold">
              <ListTree className="h-4 w-4 text-teal-700" />
              فهرس الدرس
            </p>
            <nav className="flex flex-col gap-1">
              {TOC.map((t) => (
                <button
                  key={t.id}
                  data-testid={`lesson-toc-${t.id}`}
                  onClick={() => jump(t.id)}
                  className={`min-h-[44px] rounded-lg px-3 text-right text-sm font-semibold transition ${
                    active === t.id ? "bg-teal-600/10 text-teal-700" : "text-navy-900/60 hover:bg-navy-900/5"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
            <div className="mt-5 border-t border-navy-900/10 pt-5">
              <p className="mb-3 text-xs font-bold text-navy-900/50">دروس {lesson.level} الأخرى</p>
              <div className="flex flex-col gap-1">
                {levelItems.map((li) => (
                  <Link
                    key={li.id}
                    to={`/lessons/${li.id}`}
                    data-testid={`lesson-toc-sibling-${li.id}`}
                    dir="ltr"
                    className={`min-h-[36px] truncate rounded-lg px-3 py-2 text-left font-en text-xs transition ${
                      li.id === lesson.id
                        ? "bg-navy-900/8 font-bold text-navy-900"
                        : "text-navy-900/55 hover:bg-navy-900/5"
                    }`}
                  >
                    {li.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <button
        data-testid="lesson-toc-button"
        onClick={() => setTocOpen(true)}
        className="fixed bottom-[calc(env(safe-area-inset-bottom)+20px)] left-1/2 z-40 flex h-12 -translate-x-1/2 items-center gap-2 rounded-full bg-navy-900 px-6 text-sm font-bold text-ink shadow-2xl lg:hidden"
      >
        <ListTree className="h-5 w-5 text-teal-400" />
        فهرس الدرس
      </button>

      <BottomSheet open={tocOpen} onClose={() => setTocOpen(false)} label="فهرس الدرس" testid="lesson-toc-sheet">
        <p className="mb-4 flex items-center gap-2 text-base font-bold">
          <ListTree className="h-5 w-5 text-teal-400" />
          فهرس الدرس
        </p>
        <nav className="flex flex-col">
          {TOC.map((t) => (
            <button
              key={t.id}
              data-testid={`lesson-sheet-toc-${t.id}`}
              onClick={() => jump(t.id)}
              className="flex min-h-[56px] items-center justify-between border-b border-white/5 text-right text-base font-semibold"
            >
              {t.label}
              <ChevronLeft className="h-4 w-4 text-ink-mute" />
            </button>
          ))}
        </nav>
        <button
          data-testid="lesson-sheet-library"
          onClick={() => navigate("/", { state: { to: "library" } })}
          className="mt-5 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-white/5 text-base font-bold text-ink"
        >
          <LibraryIcon className="h-5 w-5" />
          كل الدروس
        </button>
      </BottomSheet>

      <Footer />
    </div>
  );
};

export default LessonPage;
